import React, { useContext, useMemo, useState } from 'react';
import {
  Chip,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import {
  ButtonDelete,
  ButtonLoading,
  LoadingTable,
  NoDataTable,
} from 'components';
import { useMutationCustom, useQueryCustom } from 'hooks';
import { PatientDetailContext } from 'share/context';
import { formatDate } from 'share/utils';
import { PaperBorder, TableContainerStyled } from 'share/component_css';
import {
  DeletePatientDocumentMutation,
  DeletePatientDocumentMutationVariables,
  GetPatientDocumentsQuery,
  GetPatientDocumentsQueryVariables,
  PatientDocumentDownloadUrlMutation,
  PatientDocumentDownloadUrlMutationVariables,
  PatientDocumentFragment,
} from 'types.d';
import {
  DELETE_PATIENT_DOCUMENT,
  GET_PATIENT_DOCUMENTS,
  PATIENT_DOCUMENT_DOWNLOAD_URL,
} from 'gql';
import DialogUploadDocument from './DialogUploadDocument';
import { DOCUMENT_TYPE_LABEL, formatSize } from './utils';

/** Files attached to the patient, shared by clinic and DME navigators */
export const Documents: React.FC = () => {
  const patientDetailContext = useContext(PatientDetailContext);
  const patientId = patientDetailContext?.patient.owner?.idOwner;
  const [isOpenUpload, setIsOpenUpload] = useState(false);

  const { data, loading, refetch } = useQueryCustom<
    GetPatientDocumentsQuery,
    GetPatientDocumentsQueryVariables
  >({
    api: GET_PATIENT_DOCUMENTS,
    variables: { params: { patientId } },
    fetchPolicy: 'network-only',
  });
  const documents = data?.patientDocuments || [];

  const [getDownloadUrl] = useMutationCustom<
    PatientDocumentDownloadUrlMutation,
    PatientDocumentDownloadUrlMutationVariables
  >({
    api: PATIENT_DOCUMENT_DOWNLOAD_URL,
    callbackSuccess: result => {
      window.open(result.patientDocumentDownloadUrl, '_blank', 'noopener');
    },
  });

  const [deleteDocument, { loading: deleting }] = useMutationCustom<
    DeletePatientDocumentMutation,
    DeletePatientDocumentMutationVariables
  >({
    api: DELETE_PATIENT_DOCUMENT,
    textSuccess: 'Document deleted',
    callbackSuccess: () => refetch(),
  });

  const canUpload = useMemo(
    () =>
      !patientDetailContext?.isAnonymous &&
      !!patientDetailContext?.patient.owner &&
      !patientDetailContext?.isInactive,
    [patientDetailContext],
  );

  const download = (document: PatientDocumentFragment) =>
    getDownloadUrl({ variables: { params: { documentId: document._id } } });

  return (
    <>
      {isOpenUpload && patientId && (
        <DialogUploadDocument
          open={isOpenUpload}
          patientId={patientId}
          onClose={() => setIsOpenUpload(false)}
          onUploaded={() => {
            setIsOpenUpload(false);
            refetch();
          }}
        />
      )}
      {canUpload && (
        <ButtonLoading
          className="mb-8"
          Icon={<AddIcon />}
          text="Upload document"
          callbackClick={() => setIsOpenUpload(true)}
        />
      )}
      <TableContainerStyled component={PaperBorder}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Uploaded by</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && documents.length === 0 && <NoDataTable colSpan={6} />}
            {loading && <LoadingTable colSpan={6} />}
            {!loading &&
              documents.map(document => (
                <TableRow key={document._id} data-testid="patient-document">
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => download(document)}
                    >
                      {document.name}
                    </Link>
                  </TableCell>
                  <TableCell>{DOCUMENT_TYPE_LABEL[document.type]}</TableCell>
                  <TableCell>
                    {document.uploadedBy
                      ? `${document.uploadedBy.firstName} ${document.uploadedBy.lastName}`
                      : 'N/A'}
                    {document.uploadedByExternal && (
                      <Chip
                        className="ml-8"
                        label="DME"
                        size="small"
                        color="secondary"
                      />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(document.createdAt, true)}</TableCell>
                  <TableCell>{formatSize(document.size)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => download(document)}
                        aria-label="Download"
                      >
                        <GetAppIcon />
                      </IconButton>
                    </Tooltip>
                    {document.canDelete && (
                      <ButtonDelete
                        id={document._id}
                        loadingDelete={deleting}
                        deleteItem={documentId =>
                          deleteDocument({
                            variables: { params: { documentId } },
                          })
                        }
                        title="Are you sure you want to delete this document?"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainerStyled>
    </>
  );
};

export default Documents;
